import { useContext } from 'react';

import {
  faEllipsis,
  faMoneyCheckDollarPen,
} from '@awesome.me/kit-f3dc60f0d0/icons/classic/solid';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faArrowUpRightFromSquare,
  faChartSimple,
  faVideo,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLingui } from '@lingui/react/macro';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@repo/videa-components/src/index';
import { useSelector } from 'react-redux';

import { PmsType, type User } from '@/@types/global';
import routesConstants from '@/routes/routes.constants';
import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import { ExternalPatientContext } from '../constants';
import { IframeMessageType, ModalType, TrialStatus } from '../types';

interface VisitSidebarDropdownMenuItemProps {
  icon: IconProp;
  onClick: () => void;
  title: string;
}

const VisitSidebarDropdownMenuItem = ({
  icon,
  onClick,
  title,
}: VisitSidebarDropdownMenuItemProps) => (
  <DropdownMenuItem
    className="hover:!vai-bg-zinc-700 hover:!vai-text-white vai-cursor-pointer"
    onClick={onClick}
  >
    <FontAwesomeIcon icon={icon} />
    {title}
  </DropdownMenuItem>
);

interface VisitSidebarMenuProps {
  onManageSubscription: () => void;
  showPracticeAIImpact: boolean;
  trialStatus?: TrialStatus;
}

export const VisitSidebarMenu = ({
  onManageSubscription,
  showPracticeAIImpact,
  trialStatus,
}: VisitSidebarMenuProps) => {
  const { t } = useLingui();
  const { trackEvent } = useTrackEvent();
  const externalPatientContext = useContext(ExternalPatientContext);
  const pmsType = useSelector(
    (state: { login?: { user?: User } }) =>
      state.login?.user?.practice?.pmsType,
  );

  const isPaidSubscription =
    trialStatus === TrialStatus.PAID ||
    trialStatus === TrialStatus.PAID_BUT_PROVISIONAL;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="vai-bg-zinc-950 vai-text-white vai-border vai-border-zinc-800 vai-rounded-md vai-w-6 vai-h-6 vai-flex vai-items-center vai-justify-center data-[state=open]:vai-bg-zinc-800">
        <FontAwesomeIcon icon={faEllipsis} />
      </DropdownMenuTrigger>

      <DropdownMenuContent
        className="vai-w-56 vai-bg-zinc-800 vai-text-white vai-border-none"
        align="end"
      >
        {/* Hide the practice AI impact modal for paid users for now */}
        {/* {isPaidSubscription && showPracticeAIImpact && (
          <VisitSidebarDropdownMenuItem
            title={t`Practice AI Impact`}
            onClick={() => {
              trackEvent(mixpanelEvents.externalAiImpactModalOpen, {
                ...externalPatientContext,
                source: 'MENU',
              });
              window.parent.postMessage(
                {
                  type: IframeMessageType.MODAL_OPEN,
                  payload: {
                    type: ModalType.AI_IMPACT,
                    patientId: externalPatientContext.patientId,
                    imageId: externalPatientContext.imageId,
                    externalPatientId: externalPatientContext.externalPatientId,
                    externalImageId: externalPatientContext.externalImageId,
                  },
                },
                '*',
              );
            }}
            icon={faChartSimple}
          />
        )} */}
        <VisitSidebarDropdownMenuItem
          title={t`AI Onboarding`}
          onClick={() => {
            trackEvent(mixpanelEvents.externalOnboardingModalOpen, {
              ...externalPatientContext,
              source: 'MENU',
            });
            window.parent.postMessage(
              {
                type: IframeMessageType.MODAL_OPEN,
                payload: {
                  type: ModalType.ONBOARDING,
                  patientId: externalPatientContext.patientId,
                  imageId: externalPatientContext.imageId,
                  externalPatientId: externalPatientContext.externalPatientId,
                  externalImageId: externalPatientContext.externalImageId,
                },
              },
              '*',
            );
          }}
          icon={faVideo}
        />
        {/* Commenting out until we get permissions for this */}
        {/* {pmsType === PmsType.DENTRIX_ASCEND && (
          <VisitSidebarDropdownMenuItem
            title={t`Patient List`}
            onClick={() => {
              trackEvent(mixpanelEvents.externalPatientListClick, {
                ...externalPatientContext,
                source: 'MENU',
              });
              window.open(routesConstants.UPLOADER, '_blank');
            }}
            icon={faArrowUpRightFromSquare}
          />
        )} */}
        {/* Commenting out after request from HS1 */}
        {/* <VisitSidebarDropdownMenuItem
          title={t`Manage Subscription`}
          onClick={onManageSubscription}
          icon={faMoneyCheckDollarPen}
        /> */}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
